<template>
  <div
    class="absolute z-30 mt-1 w-48 bg-white dark:bg-[#1C2128] rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 text-sm"
    :class="position === 'right' ? 'right-0' : 'left-0'"
    @click.stop>

    <a v-if="canReschedule" :href="`/bookings/${booking.id}/reschedule`" target="_blank"
      class="flex items-center gap-2.5 px-3.5 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      :class="{ 'opacity-50 pointer-events-none': booking.can_be_rescheduled === false }">
      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"/>
      </svg>
      {{ I18N.application.bookings.upcoming.reschedule }}
    </a>

    <button @click="$emit('edit-notes')"
      class="w-full flex items-center gap-2.5 px-3.5 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/>
      </svg>
      {{ booking.internal_notes ? I18N.application.bookings.meeting_notes : I18N.application.bookings.add_notes }}
    </button>

    <a :href="`/attendees?attendee=${booking.attendee_email}`" data-turbo="false"
      class="flex items-center gap-2.5 px-3.5 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
      </svg>
      {{ I18N.application.bookings.attendee }}
    </a>

    <div v-if="canCancel || canArchive || canUnarchive" class="my-1 border-t border-gray-100 dark:border-gray-700"></div>

    <button v-if="canCancel" @click="$emit('cancel')"
      class="w-full flex items-center gap-2.5 px-3.5 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
      </svg>
      {{ I18N.application.events.cancel }}
    </button>

    <button v-if="canArchive" @click="$emit('archive')"
      class="w-full flex items-center gap-2.5 px-3.5 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375C2.754 3.75 2.25 4.254 2.25 4.875v1.5c0 .621.504 1.125 1.125 1.125Z"/>
      </svg>
      {{ I18N.application.bookings.upcoming.archive }}
    </button>

    <button v-if="canUnarchive" @click="$emit('unarchive')"
      class="w-full flex items-center gap-2.5 px-3.5 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"/>
      </svg>
      {{ I18N.application.bookings.upcoming.move_back }}
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useConstants } from 'composables/useConstants.js'

const props = defineProps({
  booking: { type: Object, required: true },
  category: { type: String, required: true },
  position: { type: String, default: 'left' },
})
defineEmits(['reschedule', 'edit-notes', 'cancel', 'archive', 'unarchive', 'close'])

const { I18N } = useConstants()

const canReschedule = computed(() => props.category === 'upcoming' || props.category === 'recent')
const canCancel = computed(() => props.category === 'upcoming' || props.category === 'recent')
const canArchive = computed(() => props.category === 'past' || props.category === 'canceled')
const canUnarchive = computed(() => props.category === 'archived')
</script>
